import { EmptyState, FeedbackState, SegmentedTabs } from "@c/shared";
import { useContentHealth } from "@h/useContentHealth";
import { cn } from "@u/cn";
import { groupByResource, type HealthProduct } from "@u/contentHealth";
import { buildRoute } from "@u/routes";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { HealthRow } from "./HealthRow";

type Filter = "all" | HealthProduct;

const countPill =
  "shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-warning-subtle text-warning-strong text-xs font-mono flex items-center justify-center";

export function HealthPanel({
  onClose,
  inSheet = false,
}: {
  onClose?: () => void;
  inSheet?: boolean;
}) {
  const { t } = useTranslation();
  const { issues, dismissedIssues, dismiss, restore, isError } =
    useContentHealth();
  const [filter, setFilter] = useState<Filter>("all");
  // Collapsed keys: product ("360"/"tap") + resource group key ("360:<id>").
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showDismissed, setShowDismissed] = useState(false);

  // Business first — a stale lead outranks a missing translation.
  const products = (["business", "360", "tap"] as HealthProduct[]).filter((p) =>
    issues.some((i) => i.product === p),
  );

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const shown = products.filter((p) => filter === "all" || filter === p);

  return (
    <div
      className={cn(
        "flex flex-col",
        inSheet ? "-mx-5" : "w-80 max-w-[90vw] max-h-[70dvh]",
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-sm text-foreground">{t("health.title_panel")}</span>
      </div>

      {/* Product filter — only when both products have avisos. */}
      {products.length > 1 && (
        <div className="px-3 pt-3">
          <SegmentedTabs<Filter>
            size="sm"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: t("health.all") },
              ...products.map((p) => ({
                value: p as Filter,
                label: t(`health.product_${p}`),
              })),
            ]}
          />
        </div>
      )}

      <div className={cn("mt-1", !inSheet && "overflow-y-auto")}>
        {isError ? (
          <div className="p-6">
            <FeedbackState
              variant="error"
              title={t("health.check_failed")}
              description={t("health.check_failed_desc")}
            />
          </div>
        ) : issues.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={CheckCircle2}
              title={t("health.empty_title")}
              description={t("health.empty_desc")}
            />
          </div>
        ) : shown.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t("health.none_for_filter")}
          </div>
        ) : (
          shown.map((product) => {
            const list = issues.filter((i) => i.product === product);
            const groups = groupByResource(list);
            const productCollapsed = collapsed.has(product);
            return (
              <div key={product} className="border-b border-border last:border-0">
                {/* Product section — collapsible, shows aviso count. */}
                <button
                  type="button"
                  onClick={() => toggle(product)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
                >
                  <ChevronDown
                    size={14}
                    className={cn(
                      "shrink-0 text-muted-foreground transition-transform",
                      productCollapsed && "-rotate-90",
                    )}
                  />
                  <span className="flex-1 text-sm font-medium text-foreground">
                    {t(`health.product_${product}`)}
                  </span>
                  <span className={countPill}>{list.length}</span>
                </button>

                {!productCollapsed &&
                  groups.map((g) => {
                    const itemCollapsed = collapsed.has(g.key);
                    return (
                      <div key={g.key} className="border-t border-border/50">
                        {/* Resource item — also collapsible, shows its count. */}
                        <button
                          type="button"
                          onClick={() => toggle(g.key)}
                          className="w-full flex items-center gap-2 pl-8 pr-4 py-2 text-left hover:bg-muted/30 transition-colors"
                        >
                          <ChevronDown
                            size={12}
                            className={cn(
                              "shrink-0 text-muted-foreground transition-transform",
                              itemCollapsed && "-rotate-90",
                            )}
                          />
                          <span className="flex-1 min-w-0 truncate text-xs font-medium text-muted-foreground">
                            {g.resourceTitle}
                          </span>
                          <span className={countPill}>{g.issues.length}</span>
                        </button>

                        {!itemCollapsed && (
                          <div className="divide-y divide-border/60">
                            {g.issues.map((issue) => (
                              <HealthRow
                                key={issue.id}
                                issue={issue}
                                onNavigate={onClose}
                                onDismiss={dismiss}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}

        {/* Dismissed avisos — collapsed by default, restorable. */}
        {dismissedIssues.length > 0 && (
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => setShowDismissed((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-xs text-muted-foreground hover:bg-muted/40 transition-colors"
            >
              <ChevronDown
                size={14}
                className={cn(
                  "shrink-0 transition-transform",
                  !showDismissed && "-rotate-90",
                )}
              />
              <span className="flex-1">
                {t("health.dismissed", { count: dismissedIssues.length })}
              </span>
            </button>
            {showDismissed && (
              <div className="divide-y divide-border/60">
                {dismissedIssues.map((issue) => (
                  <HealthRow
                    key={issue.id}
                    issue={issue}
                    onNavigate={onClose}
                    onRestore={restore}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Link
        to={buildRoute.health()}
        onClick={onClose}
        className="border-t border-border px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-muted/40 transition-colors"
      >
        {t("health.see_all")}
      </Link>
    </div>
  );
}
